// The office shell's data listener: jobs started since yesterday directly, every other job and
// customer change through the change index (changeFeed.js), each applied to this device's copy,
// the in-memory jobsByWorkOrder map and the rows on screen (glowing what another user changed).
// Moved out of app.js unchanged (docs/split-plan.md, session 7).
import debounce from 'lodash/debounce';
import { watch as watchChanges } from './changeFeed';
import { formatDateToMMDDYYYY, formatDateToYYYYMMDD } from './date-format';
import { database } from './initFirebase';
import { getJobData, refreshOpenLaborParts } from './laborParts';
import { glow, recentInteraction } from './liveView';
import { techsLabel } from './techNames';
import { escapeHTML, getDB, idbGet, notifyLocalDataChanged, toastMessage } from './utilities';
import { updateTable } from './woFilters';
import { createNewTableRow, createOperationRow, refreshWorkOrderRow, syncOpenOperationsPanel } from './woTable';
import { onChildAdded, orderByChild, query, ref, startAt } from 'firebase/database';

let shell = null;
export function setDataListenerDeps(deps) { shell = deps; }

let listenersInitialized = false;

const jobInfoCache = new WeakMap();

function getJobInfoCached(el) {
  if (!el) return null;

  const cached = jobInfoCache.get(el);
  if (cached) return cached;

  const raw = el.getAttribute('data-jobInfo');
  if (!raw) return null;

  try {
    const obj = JSON.parse(raw);
    jobInfoCache.set(el, obj);
    return obj;
  } catch (e) {
    return null;
  }
}

function setJobInfoCached(el, obj) {
  if (!el || !obj) return;
  jobInfoCache.set(el, obj);
  el.setAttribute('data-jobInfo', JSON.stringify(obj));
}

function mergeJobInfoCached(el, patch) {
  const obj = getJobInfoCached(el) || {};
  Object.assign(obj, patch);
  setJobInfoCached(el, obj);
  return obj;
}

// A fresh device's initial sync can stream in the entire job history one
// onChildAdded event at a time; updateTable() rescans/re-sorts the whole
// table on every call, so calling it per-job made the initial sync O(n^2).
// Debouncing collapses a whole burst of additions into one pass at the end.
const scheduleTableUpdate = debounce(() => updateTable(), 250);

// Live (docs/live-updates-plan.md): a work order / operation row another user added or changed
// glows once. Not during the first seconds (the cache catching up), and not right after this
// user's own click or typing (their own write).
const jobsLiveSince = Date.now();
export function glowJobRows(rows) {
  if (Date.now() - jobsLiveSince < 8000 || recentInteraction()) return;
  glow(rows.filter(Boolean).map(el => {
    if (!el.dataset.key) el.dataset.key = el.dataset.id || el.dataset.workorder || 'row';
    return el;
  }));
}

export async function initializeDataListener() {

  if (listenersInitialized) return;
  listenersInitialized = true;

  const jobsRef = ref(database, 'data');

  // Jobs started from yesterday on, listened to directly so a job this user adds shows at
  // once (Firebase echoes its own writes to a listener). Everything else, including other
  // users' changes and removals, comes through the change index below (changeFeed.js):
  // listening to all of /data made Firebase send every job (MBs) on every load.
  // One day back, not today: this is a UTC date, but writers like the AI assistant and Fuel
  // compliance WOs stamp Start_Date in local time — after ~8pm Eastern, UTC is already
  // tomorrow. Replayed older jobs are skipped by jobExistsInIndexedDB() below.
  const startDate = new Date(Date.now() - 86400000).toISOString().split('T')[0];

  const recentJobsQuery = query(
    jobsRef,
    orderByChild('Start_Date'),
    startAt(startDate)
  );

  const jobAdded = async (jobId, val) => {
    if (jobId === 'lastUpdated' || !val || typeof val !== 'object') return;

    const newJob = { ...val, Job_ID: jobId, id: jobId };

    try {
      const jobExists = await jobExistsInIndexedDB(jobId);

      if (!jobExists) {
        await addNewJobDB(newJob);
        notifyLocalDataChanged('jobData');
        await createNewTableRow(newJob);

        // Realtime insert into the Operations tab — same row builder as the
        // rendered list, so a job added while the tab is open matches
        // immediately instead of reverting to old card styling on reload.
        const jobListDiv = document.getElementById('jobList');
        if (jobListDiv && !jobListDiv.querySelector(`.job-item[data-id="${CSS.escape(jobId)}"]`)) {
          const newOpRow = createOperationRow(newJob);
          jobListDiv.appendChild(newOpRow);
          glowJobRows([newOpRow]);
          toastMessage('New job added', 'green');
          const notificationList = document.getElementById('notification-list');
          if (notificationList) {
            const notificationJob = document.createElement('div');
            notificationJob.className = 'notification-job';
            notificationJob.innerHTML = `<p>${escapeHTML(newJob.Customer_Name)} / ${escapeHTML(newJob.Boat_Name)}</p>: <p>${escapeHTML(newJob.Description)}</p> (Date: ${escapeHTML(newJob.Start_Date)})`;
            notificationList.appendChild(notificationJob);
            const notificationCount = document.getElementById('notification-count');
            if (notificationCount) notificationCount.innerHTML = parseInt(notificationCount.innerHTML || '0') + 1;
          }
        }

        scheduleTableUpdate();

        // Keep in-memory map in sync. Pushed idempotently by Job_ID: Firebase RTDB applies a
        // client's own writes to its local cache (firing this same onChildAdded) before that
        // write's own promise resolves, so this can race addJob()'s optimistic push below and
        // double the entry if both push unconditionally — this is what caused ops added via the
        // Add-Operation modal to render twice in the sidebar.
        if (shell.jobsByWorkOrder && newJob.Category?.toString() !== 'Declined') {
          const wn = newJob.Work_Order;
          if (!shell.jobsByWorkOrder.has(wn)) shell.jobsByWorkOrder.set(wn, []);
          const arr = shell.jobsByWorkOrder.get(wn);
          if (!arr.some(j => j.Job_ID === newJob.Job_ID || j.id === newJob.Job_ID)) arr.push(newJob);
          syncOpenOperationsPanel(wn);
        }
      }
    } catch (error) {
      console.error('Failed to process job:', jobId, error);
    }
  };
  onChildAdded(recentJobsQuery, snapshot => jobAdded(snapshot.key, snapshot.val()));


const jobChanged = async (jobId, updatedJob) => {

  if (jobId !== 'lastUpdated' && updatedJob !== null && typeof updatedJob === 'object') {
    if (!updatedJob.Job_ID) updatedJob.Job_ID = jobId;
    if (!updatedJob.id) updatedJob.id = jobId;
    const workOrder = updatedJob.Work_Order;
    const oldJob = await getJobData(jobId);

    const changedKeys = [];
    if (oldJob) {
      for (const key in updatedJob) {
        if (updatedJob[key] !== oldJob[key]) {
          changedKeys.push(key, updatedJob[key]);
        }
      }
    }

    await updateEntireJobDB(updatedJob);
    notifyLocalDataChanged('jobData');
    shell.refreshOpenOpPanel(updatedJob);
    refreshOpenLaborParts(updatedJob);

    // Keep in-memory map in sync
    if (shell.jobsByWorkOrder) {
      const wn = updatedJob.Work_Order;
      // Moved to another work order (opPanel Move, possibly on another device): drop it from the
      // old one, or that WO's card keeps counting it until a reload.
      const oldWn = oldJob?.Work_Order;
      if (oldWn != null && String(oldWn) !== String(wn) && shell.jobsByWorkOrder.has(oldWn)) {
        const rest = shell.jobsByWorkOrder.get(oldWn).filter(j => j.id !== jobId && j.Job_ID !== jobId);
        rest.length ? shell.jobsByWorkOrder.set(oldWn, rest) : shell.jobsByWorkOrder.delete(oldWn);
        refreshWorkOrderRow(oldWn);
        syncOpenOperationsPanel(oldWn);
      }
      if (updatedJob.Category?.toString() === 'Declined') {
        if (shell.jobsByWorkOrder.has(wn)) {
          const arr = shell.jobsByWorkOrder.get(wn).filter(j => j.id !== jobId && j.Job_ID !== jobId);
          arr.length ? shell.jobsByWorkOrder.set(wn, arr) : shell.jobsByWorkOrder.delete(wn);
        }
      } else {
        if (!shell.jobsByWorkOrder.has(wn)) shell.jobsByWorkOrder.set(wn, []);
        const arr = shell.jobsByWorkOrder.get(wn);
        const idx = arr.findIndex(j => j.id === jobId || j.Job_ID === jobId);
        if (idx >= 0) arr[idx] = updatedJob; else arr.push(updatedJob);
      }
    }

    try {
      const jobItemSelector = '.job-item[data-id="' + jobId + '"]';
      const jobItems = document.querySelectorAll(jobItemSelector);

      const workOrderRow = workOrder
        ? document.querySelector('.table-item[data-workorder="' + workOrder + '"]')
        : null;

      const opRow = document.querySelector('.op-table-data-row[data-id="' + jobId + '"]');

      const notesButtons = document.querySelectorAll(jobItemSelector + ' .job-notes-button');

      if (jobItems.length) {
        jobItems.forEach(item => {
          const operationData = mergeJobInfoCached(item, updatedJob);

          if (updatedJob.Schedule_Date && updatedJob.Schedule_Date !== '') {
            const scheduleDate = formatDateToYYYYMMDD(updatedJob.Schedule_Date);
            const startDate = formatDateToMMDDYYYY(updatedJob.Schedule_Date);

            const startDateElement = item.querySelector('.start-date-info');
            if (startDateElement) {
              startDateElement.innerHTML = `<strong>Start Date: </strong>${startDate}`;
            }

            const scheduleDateElement = item.querySelector('.schedule-date-info');
            if (scheduleDateElement) {
              scheduleDateElement.value = scheduleDate;
            }
          } else if (updatedJob.Start_Date) {
            const date = formatDateToMMDDYYYY(updatedJob.Start_Date);

            const startDateElement = item.querySelector('.start-date-info');
            if (startDateElement) {
              startDateElement.innerHTML = `<strong>Start Date: </strong>${date}`;
            }
          }

          const techSelectElement = item.querySelector('.op-tech-select');
          if (techSelectElement) {
            techSelectElement.textContent = techsLabel(updatedJob.Techs) || "Unassigned";
            techSelectElement.classList.toggle('op-tech-unassigned', !updatedJob.Techs);
          }

          const boatNameElement = item.querySelector('.boat-name-jobs');
          if (boatNameElement) {
            boatNameElement.textContent = updatedJob.Boat_Name || "";
          }

          // ensure attribute stays in sync even if other code reads it immediately
          setJobInfoCached(item, operationData);
        });
      }

      if (workOrderRow) {
        const workOrderData = getJobInfoCached(workOrderRow) || {};

        // The card is drawn from one representative operation (createTableRow). Ops converted
        // from estimates or found by techs carry their own Short_Description, so only that op
        // may retitle the card — any other op's edit used to rename the whole work order.
        if (!workOrderData.Job_ID || workOrderData.Job_ID === jobId) {
          workOrderData.Short_Description = updatedJob.Short_Description;
          workOrderRow.querySelector('.work-order-description').textContent = updatedJob.Short_Description || '';

          if (updatedJob.Schedule_Date && updatedJob.Schedule_Date !== '') {
            workOrderData.Start_Date = updatedJob.Schedule_Date;
          } else {
            workOrderData.Start_Date = updatedJob.Start_Date;
          }
        }

        if (updatedJob.Boat_Name) workOrderRow.querySelector('.boat-name-jobs').textContent = updatedJob.Boat_Name;

        setJobInfoCached(workOrderRow, workOrderData);
        // Recomputes the progress bar / attention slot / "Next" date from the
        // now-updated jobsByWorkOrder map (kept in sync just above).
        refreshWorkOrderRow(workOrder);
      }

      if (opRow) {
        setJobInfoCached(opRow, updatedJob);
      }
      syncOpenOperationsPanel(workOrder);

      // oldJob is null when this device never cached the op — still a status change.
      const oldStatus = oldJob?.Status;
      if (oldStatus !== 'Ready to Bill' && updatedJob.Status === 'Ready to Bill') {
        shell.submitOpen(jobId, workOrder, { fromListener: true });
        shell.changeStatus(updatedJob.Job_ID);
      }

      if (oldStatus !== 'In Progress' && updatedJob.Status === 'In Progress') {
        shell.changeStatus(updatedJob.Job_ID, "In Progress");
      }

      if (oldStatus !== updatedJob.Status) {
        shell.changeStatus(updatedJob.Job_ID, updatedJob.Status);
        shell.updateOperationTableUI(jobId, updatedJob);
      }

      if (updatedJob.Notes) {
        notesButtons.forEach(btn => {
          const img = btn.querySelector('img');
          if (img) {
            img.style.filter = 'brightness(0) saturate(100%) invert(15%) sepia(95%) saturate(6932%) hue-rotate(359deg) brightness(94%) contrast(113%)';
          }
        });
      } else {
        notesButtons.forEach(btn => {
          const img = btn.querySelector('img');
          if (img) img.style.filter = 'none';
        });
      }

      // Only fields people see — not timestamps the app stamps on its own.
      const seen = changedKeys.filter((k, i) => i % 2 === 0 && !/^(lastUpdated|Last_Updated|updatedAt|Updated_At)$/i.test(k));
      if (seen.length) glowJobRows([...jobItems, workOrderRow, opRow]);

      console.log('Job updated in IndexedDB');
    } catch (error) {
      console.error('Failed to update job in IndexedDB', error);
    }
  }
};


const jobRemoved = async (jobId, removedJob) => {
  if (jobId === 'lastUpdated') return;
  console.log('Job removed:', jobId);

  // Keep in-memory map in sync
  if (shell.jobsByWorkOrder) {
    if (removedJob?.Work_Order) {
      const wn = removedJob.Work_Order;
      if (shell.jobsByWorkOrder.has(wn)) {
        const arr = shell.jobsByWorkOrder.get(wn).filter(j => j.id !== jobId && j.Job_ID !== jobId);
        arr.length ? shell.jobsByWorkOrder.set(wn, arr) : shell.jobsByWorkOrder.delete(wn);
      }
    }
  }

  await shell.deleteOperationOtherUser(removedJob);
  notifyLocalDataChanged('jobData');
};

// Other users' changes and removals (and this user's, a moment later), job by job.
watchChanges({
  root: 'data',
  store: 'jobData',
  onChange: async (jobId, job) => {
    if (job && typeof job === 'object') {
      if (await jobExistsInIndexedDB(jobId)) await jobChanged(jobId, job);
      else await jobAdded(jobId, job);
    } else {
      const old = await getJobData(jobId);
      if (old) await jobRemoved(jobId, old);
    }
  },
});

const customerChanged = async (customerId, updatedCustomer) => {

  if(customerId === 'lastUpdated' || customerId === '') { return };

  try {
    await updateEntireCustomerDB(updatedCustomer);
    notifyLocalDataChanged('customerData');

    if(document.querySelector('.customer-item[data-id="' + customerId + '"]')) {
      let customerData = JSON.parse(document.querySelector('.customer-item[data-id="' + customerId + '"]').getAttribute('data-customerInfo'));
      customerData = updatedCustomer;
      document.querySelector('.customer-item[data-id="' + customerId + '"]').setAttribute('data-customerInfo', JSON.stringify(customerData));
    };

    console.log('Customer updated in IndexedDB');
  } catch (error) {
    console.error('Failed to update customer in IndexedDB', error);
  }
};

// A customer this device doesn't have yet (or has an older copy of).
const customerAdded = async (customerId, val) => {
  const newCustomer = { ...val, Customer_ID: customerId };

  try {
    const cached = await idbGet('customerData', customerId);
    if (!cached) {
      await addNewCustomerToDB(newCustomer);
      notifyLocalDataChanged('customerData');
    } else if (JSON.stringify(cached) !== JSON.stringify({ ...newCustomer, id: customerId })) {
      // Record already exists locally but RTDB has newer data (e.g. edited on
      // another device while this one was offline/closed) — refresh the cache.
      await updateEntireCustomerDB(newCustomer);
      notifyLocalDataChanged('customerData');
    }
  } catch (error) {
    console.error('Failed to process new customer:', customerId, error);
  }
};

const customerRemoved = async (customerId) => {
  console.log('Customer removed:', customerId);

  try {
    await deleteCustomerFromDB(customerId);
    notifyLocalDataChanged('customerData');
    console.log('Customer removed from IndexedDB');
  } catch (error) {
    console.error('Failed to remove customer from IndexedDB', error);
  }
};

// Customers, through the change index too (all of /customers is MBs).
watchChanges({
  root: 'customers',
  store: 'customerData',
  onChange: async (customerId, customer) => {
    if (!customerId) return;
    if (!customer || typeof customer !== 'object') return customerRemoved(customerId);
    if (await idbGet('customerData', customerId)) return customerChanged(customerId, customer);
    return customerAdded(customerId, customer);
  },
});

}

async function jobExistsInIndexedDB(jobId) {
  try {
    const db = await getDB();
    const tx = db.transaction("jobData", "readonly");
    const store = tx.objectStore("jobData");

    return await new Promise((resolve, reject) => {
      const req = store.count(jobId);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result > 0);
    });
  } catch (error) {
    console.error("Error opening database", error);
    throw error;
  }
}


async function updateEntireJobDB(jobData) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("jobData", "readwrite");
      const objectStore = transaction.objectStore("jobData");

      if (!jobData || !jobData.Job_ID) {
        reject("Job data missing Job_ID");
        return;
      }

      jobData.id = jobData.Job_ID;

      const updateRequest = objectStore.put(jobData);

      updateRequest.onsuccess = function () {
        shell.updateJobRow(jobData.Job_ID, 'Techs', jobData.Techs, jobData);
        resolve();
      };

      updateRequest.onerror = function (error) {
        console.error("Error updating data", error);
        reject(error);
      };

    } catch (error) {
      console.error("Error opening database", error);
      reject(error);
    }
  });
}


async function addNewJobDB(jobData) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("jobData", "readwrite");
      const objectStore = transaction.objectStore("jobData");

      if (!jobData || !jobData.Job_ID) {
        reject("Job data missing Job_ID");
        return;
      }

      jobData.id = jobData.Job_ID;

      const addRequest = objectStore.add(jobData);

      addRequest.onsuccess = function () {
        console.log(`New job added with Job_ID ${jobData.Job_ID}`);
        resolve();
      };

      addRequest.onerror = function (error) {
        console.error("Error adding new job", error);
        reject(error);
      };

    } catch (error) {
      console.error("Error opening database", error);
      reject(error);
    }
  });
}



async function addNewCustomerToDB(customerData) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("customerData", "readwrite");
      const objectStore = transaction.objectStore("customerData");

      if (!customerData || !customerData.Customer_ID) {
        reject("Customer data missing Customer_ID");
        return;
      }

      customerData.id = customerData.Customer_ID;

      const addRequest = objectStore.put(customerData);

      addRequest.onsuccess = function () {
        resolve();
      };

      addRequest.onerror = function (error) {
        console.error("Error adding new customer", error);
        reject(error);
      };

    } catch (error) {
      console.error("Error opening database", error);
      reject(error);
    }
  });
}


async function updateEntireCustomerDB(customerData) {
  if (!customerData || !customerData.Customer_ID) {
    return;
  }

  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("customerData", "readwrite");
      const objectStore = transaction.objectStore("customerData");

      customerData.id = customerData.Customer_ID;

      const updateRequest = objectStore.put(customerData);

      updateRequest.onsuccess = function () {
        resolve();
      };

      updateRequest.onerror = function (error) {
        console.error("Error updating customer data", error);
        reject(error);
      };

    } catch (error) {
      console.error("Error opening database", error);
      reject(error);
    }
  });
}

async function deleteCustomerFromDB(customerId) {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await getDB();
      const transaction = db.transaction("customerData", "readwrite");
      const objectStore = transaction.objectStore("customerData");

      const deleteRequest = objectStore.delete(customerId);

      deleteRequest.onsuccess = function () {
        resolve();
      };

      deleteRequest.onerror = function (error) {
        console.error("Error deleting customer data", error);
        reject(error);
      };

    } catch (error) {
      console.error("Error opening database", error);
      reject(error);
    }
  });
}
